/**
 * Armazenamento local da Price Activity (snapshots + changes).
 *
 * Ordem de fiabilidade:
 * 1. IndexedDB
 * 2. localStorage (sobrevive a falhas IDB / reload com fallback memória)
 * 3. memória (sessão)
 */

import type {
  PriceActivityChange,
  PriceActivityRepository,
  PriceActivitySnapshot,
} from '@/lib/price-activity-types';

const DB_NAME = 'xat-ajuda-fairtrade-price-activity';
const DB_VERSION = 1;
const STORE_SNAPSHOTS = 'snapshots';
const STORE_CHANGES = 'changes';

const LS_SNAPSHOTS_KEY = 'xat-ajuda-ft-pa-snapshots-v1';
const LS_CHANGES_KEY = 'xat-ajuda-ft-pa-changes-v1';

type AdapterKind = 'indexeddb' | 'memory';

let adapterKind: AdapterKind = 'indexeddb';
let dbPromise: Promise<IDBDatabase> | null = null;

const memorySnapshots = new Map<number, PriceActivitySnapshot>();
const memoryChanges = new Map<string, PriceActivityChange>();

export function getPriceActivityAdapterKind(): AdapterKind {
  return adapterKind;
}

function canUseLocalStorage(): boolean {
  try {
    return typeof localStorage !== 'undefined';
  } catch {
    return false;
  }
}

function readLsSnapshots(): PriceActivitySnapshot[] {
  if (!canUseLocalStorage()) return [];
  try {
    const raw = localStorage.getItem(LS_SNAPSHOTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (item): item is PriceActivitySnapshot =>
        Boolean(item && typeof item === 'object' && typeof (item as PriceActivitySnapshot).powerId === 'number')
    );
  } catch {
    return [];
  }
}

function writeLsSnapshots(snapshots: PriceActivitySnapshot[]): void {
  if (!canUseLocalStorage()) return;
  try {
    localStorage.setItem(LS_SNAPSHOTS_KEY, JSON.stringify(snapshots));
  } catch {
    /* quota / private mode */
  }
}

function readLsChanges(): PriceActivityChange[] {
  if (!canUseLocalStorage()) return [];
  try {
    const raw = localStorage.getItem(LS_CHANGES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (item): item is PriceActivityChange =>
        Boolean(item && typeof item === 'object' && typeof (item as PriceActivityChange).id === 'string')
    );
  } catch {
    return [];
  }
}

function writeLsChanges(changes: PriceActivityChange[]): void {
  if (!canUseLocalStorage()) return;
  try {
    localStorage.setItem(LS_CHANGES_KEY, JSON.stringify(changes));
  } catch {
    /* quota / private mode */
  }
}

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_SNAPSHOTS)) {
        db.createObjectStore(STORE_SNAPSHOTS, { keyPath: 'powerId' });
      }
      if (!db.objectStoreNames.contains(STORE_CHANGES)) {
        const store = db.createObjectStore(STORE_CHANGES, { keyPath: 'id' });
        store.createIndex('category', 'category', { unique: false });
        store.createIndex('timestamp', 'timestamp', { unique: false });
        store.createIndex('powerId', 'powerId', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
  }).catch((err) => {
    // Permite nova tentativa no próximo acesso.
    dbPromise = null;
    throw err;
  });

  return dbPromise;
}

function idbReq<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });
}

function idbTxDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
    tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
  });
}

async function withDb<T>(fn: (db: IDBDatabase) => Promise<T>): Promise<T> {
  try {
    const db = await openDb();
    adapterKind = 'indexeddb';
    return await fn(db);
  } catch (err) {
    adapterKind = 'memory';
    dbPromise = null;
    throw err instanceof Error ? err : new Error('IndexedDB unavailable');
  }
}

function hydrateMemoryFromLs(): void {
  if (memorySnapshots.size === 0) {
    for (const s of readLsSnapshots()) memorySnapshots.set(s.powerId, s);
  }
  if (memoryChanges.size === 0) {
    for (const c of readLsChanges()) memoryChanges.set(c.id, c);
  }
}

const memoryRepo: PriceActivityRepository = {
  async getSnapshot(powerId) {
    hydrateMemoryFromLs();
    return memorySnapshots.get(powerId);
  },
  async getAllSnapshots() {
    hydrateMemoryFromLs();
    return Array.from(memorySnapshots.values());
  },
  async putSnapshots(snapshots) {
    memorySnapshots.clear();
    for (const s of snapshots) memorySnapshots.set(s.powerId, s);
    writeLsSnapshots(snapshots);
  },
  async deleteSnapshotsNotIn(powerIds) {
    hydrateMemoryFromLs();
    const keep = new Set(powerIds);
    for (const id of Array.from(memorySnapshots.keys())) {
      if (!keep.has(id)) memorySnapshots.delete(id);
    }
    writeLsSnapshots(Array.from(memorySnapshots.values()));
  },
  async listChanges() {
    hydrateMemoryFromLs();
    return Array.from(memoryChanges.values());
  },
  async listChangesByCategory(category) {
    hydrateMemoryFromLs();
    return Array.from(memoryChanges.values()).filter((c) => c.category === category);
  },
  async getLatestChangeForPower(powerId) {
    hydrateMemoryFromLs();
    const list = Array.from(memoryChanges.values())
      .filter((c) => c.powerId === powerId)
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    return list[0];
  },
  async addChanges(changes) {
    hydrateMemoryFromLs();
    for (const c of changes) memoryChanges.set(c.id, c);
    writeLsChanges(Array.from(memoryChanges.values()));
  },
  async trimChanges({ retentionDays, maxPerCategory }) {
    hydrateMemoryFromLs();
    const cutoff = Date.now() - retentionDays * 24 * 60 * 60 * 1000;
    for (const [id, c] of Array.from(memoryChanges.entries())) {
      if (new Date(c.timestamp).getTime() < cutoff) memoryChanges.delete(id);
    }

    const byCat = new Map<string, PriceActivityChange[]>();
    for (const c of memoryChanges.values()) {
      const list = byCat.get(c.category) ?? [];
      list.push(c);
      byCat.set(c.category, list);
    }
    for (const [, list] of byCat) {
      list.sort(
        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
      for (const drop of list.slice(maxPerCategory)) {
        memoryChanges.delete(drop.id);
      }
    }
    writeLsChanges(Array.from(memoryChanges.values()));
  },
};

async function readSnapshotsPreferIdb(): Promise<PriceActivitySnapshot[]> {
  try {
    const fromIdb = await withDb(async (db) => {
      const tx = db.transaction(STORE_SNAPSHOTS, 'readonly');
      return idbReq(tx.objectStore(STORE_SNAPSHOTS).getAll()) as Promise<
        PriceActivitySnapshot[]
      >;
    });
    if (fromIdb.length > 0) {
      writeLsSnapshots(fromIdb);
      return fromIdb;
    }
  } catch {
    /* fallback below */
  }

  const fromLs = readLsSnapshots();
  if (fromLs.length > 0) {
    for (const s of fromLs) memorySnapshots.set(s.powerId, s);
    // Tenta repor no IDB em background.
    void withDb(async (db) => {
      const tx = db.transaction(STORE_SNAPSHOTS, 'readwrite');
      const store = tx.objectStore(STORE_SNAPSHOTS);
      for (const s of fromLs) store.put(s);
      await idbTxDone(tx);
    }).catch(() => undefined);
    return fromLs;
  }

  return memoryRepo.getAllSnapshots();
}

async function readChangesPreferIdb(): Promise<PriceActivityChange[]> {
  try {
    const fromIdb = await withDb(async (db) => {
      const tx = db.transaction(STORE_CHANGES, 'readonly');
      return idbReq(tx.objectStore(STORE_CHANGES).getAll()) as Promise<PriceActivityChange[]>;
    });
    if (fromIdb.length > 0) {
      writeLsChanges(fromIdb);
      return fromIdb;
    }
  } catch {
    /* fallback */
  }

  const fromLs = readLsChanges();
  if (fromLs.length > 0) {
    for (const c of fromLs) memoryChanges.set(c.id, c);
    void withDb(async (db) => {
      const tx = db.transaction(STORE_CHANGES, 'readwrite');
      const store = tx.objectStore(STORE_CHANGES);
      for (const c of fromLs) store.put(c);
      await idbTxDone(tx);
    }).catch(() => undefined);
    return fromLs;
  }

  return memoryRepo.listChanges();
}

const idbRepo: PriceActivityRepository = {
  async getSnapshot(powerId) {
    const all = await readSnapshotsPreferIdb();
    return all.find((s) => s.powerId === powerId);
  },

  async getAllSnapshots() {
    return readSnapshotsPreferIdb();
  },

  async putSnapshots(snapshots) {
    writeLsSnapshots(snapshots);
    for (const s of snapshots) memorySnapshots.set(s.powerId, s);
    try {
      await withDb(async (db) => {
        const tx = db.transaction(STORE_SNAPSHOTS, 'readwrite');
        const store = tx.objectStore(STORE_SNAPSHOTS);
        for (const s of snapshots) store.put(s);
        await idbTxDone(tx);
      });
    } catch {
      /* LS + memória já gravados */
    }
  },

  async deleteSnapshotsNotIn(powerIds) {
    const keep = new Set(powerIds);
    const current = await readSnapshotsPreferIdb();
    const next = current.filter((s) => keep.has(s.powerId));
    await this.putSnapshots(next);
  },

  async listChanges() {
    return readChangesPreferIdb();
  },

  async listChangesByCategory(category) {
    const all = await readChangesPreferIdb();
    return all.filter((c) => c.category === category);
  },

  async getLatestChangeForPower(powerId) {
    const list = (await readChangesPreferIdb())
      .filter((c) => c.powerId === powerId)
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    return list[0];
  },

  async addChanges(changes) {
    if (changes.length === 0) return;
    const current = await readChangesPreferIdb();
    const map = new Map(current.map((c) => [c.id, c]));
    for (const c of changes) map.set(c.id, c);
    const merged = Array.from(map.values());
    writeLsChanges(merged);
    for (const c of changes) memoryChanges.set(c.id, c);
    try {
      await withDb(async (db) => {
        const tx = db.transaction(STORE_CHANGES, 'readwrite');
        const store = tx.objectStore(STORE_CHANGES);
        for (const c of changes) store.put(c);
        await idbTxDone(tx);
      });
    } catch {
      /* LS já gravado */
    }
  },

  async trimChanges({ retentionDays, maxPerCategory }) {
    const all = await readChangesPreferIdb();
    const cutoff = Date.now() - retentionDays * 24 * 60 * 60 * 1000;
    let remaining = all.filter((c) => new Date(c.timestamp).getTime() >= cutoff);

    const byCat = new Map<string, PriceActivityChange[]>();
    for (const c of remaining) {
      const list = byCat.get(c.category) ?? [];
      list.push(c);
      byCat.set(c.category, list);
    }

    const keepIds = new Set<string>();
    for (const [, list] of byCat) {
      list.sort(
        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
      for (const c of list.slice(0, maxPerCategory)) keepIds.add(c.id);
    }
    remaining = remaining.filter((c) => keepIds.has(c.id));

    writeLsChanges(remaining);
    memoryChanges.clear();
    for (const c of remaining) memoryChanges.set(c.id, c);

    try {
      await withDb(async (db) => {
        const tx = db.transaction(STORE_CHANGES, 'readwrite');
        const store = tx.objectStore(STORE_CHANGES);
        const existing = (await idbReq(store.getAll())) as PriceActivityChange[];
        const keep = new Set(remaining.map((c) => c.id));
        for (const c of existing) {
          if (!keep.has(c.id)) store.delete(c.id);
        }
        for (const c of remaining) store.put(c);
        await idbTxDone(tx);
      });
    } catch {
      /* LS já actualizado */
    }
  },
};

let repo: PriceActivityRepository = idbRepo;

export function getPriceActivityRepository(): PriceActivityRepository {
  return repo;
}

/** Testes / mock provider — troca o repo por trás da mesma interface. */
export function setPriceActivityRepository(next: PriceActivityRepository): void {
  repo = next;
}

export function resetPriceActivityRepository(): void {
  repo = idbRepo;
}
